import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  acceptAllConsent,
  allowsCategory,
  hasConsentDecision,
  readConsent,
  rejectNonEssentialConsent,
  writeConsent,
} from "@/lib/cookieConsent";
import { disablePosthogAnalytics, enablePosthogAnalytics } from "@/lib/posthogLoader";
import { clearAnalyticsSafeMode, isAnalyticsSafeMode } from "@/lib/clientRecovery";

const CookieConsentContext = createContext(null);

function applyStatisticsConsent(enabled) {
  try {
    if (enabled) {
      enablePosthogAnalytics();
    } else {
      disablePosthogAnalytics();
      try {
        localStorage.removeItem("autopark_visit_sid");
      } catch {
        /* ignore */
      }
    }
  } catch (err) {
    if (typeof console !== "undefined") {
      console.warn("[AutoPark] analytics consent apply failed:", err);
    }
  }
}

function syncConsentFromStorage() {
  return readConsent();
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsentState] = useState(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const sync = () => {
      const latest = syncConsentFromStorage();
      setConsentState(latest);
      if (hasConsentDecision(latest) && latest.statistics && !isAnalyticsSafeMode()) {
        applyStatisticsConsent(true);
      }
    };
    sync();
    window.addEventListener("focus", sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const commit = useCallback((next) => {
    setConsentState(next);
    if (next.statistics && isAnalyticsSafeMode()) {
      clearAnalyticsSafeMode();
    }
    applyStatisticsConsent(!!next.statistics);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("autopark:cookie-consent", { detail: next }));
    }
  }, []);

  const acceptAll = useCallback(() => {
    commit(acceptAllConsent());
    setSettingsOpen(false);
  }, [commit]);

  const rejectOptional = useCallback(() => {
    commit(rejectNonEssentialConsent());
    setSettingsOpen(false);
  }, [commit]);

  const saveCustom = useCallback(
    (choices) => {
      commit(
        writeConsent({
          functional: !!choices.functional,
          statistics: !!choices.statistics,
          marketing: false,
        }),
      );
      setSettingsOpen(false);
    },
    [commit],
  );

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const allows = useCallback((category) => allowsCategory(category, consent), [consent]);

  const value = useMemo(
    () => ({
      consent,
      decided: hasConsentDecision(consent),
      settingsOpen,
      acceptAll,
      rejectOptional,
      saveCustom,
      openSettings,
      closeSettings,
      allows,
    }),
    [consent, settingsOpen, acceptAll, rejectOptional, saveCustom, openSettings, closeSettings, allows],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent requires CookieConsentProvider");
  return ctx;
}
